import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TextField, TextFieldType } from '@lux/form-elements';
import { MessageDialog, Size } from '@lux/dialog';
import { AccountSecurity } from '@lux/graphics';
import { useTranslate } from '@pegasus/translation';
import { validateEmail } from '@pegasus/validation';
import { FieldWrapper, AlignTextCenter } from './styled';
import { getSSOUrl } from './service';
import { track, TrackEvent } from './helpers';
import { loadFacebookSdk, setFacebookUserEmailTo, fetchFacebookToken, getFacebookFormErrors, setFacebookUserEmailUpdateFailed, useReducerState, } from './modules/facebookLogin';
const isPasswordEmailChanged = (passwordResetEmail, customerEmail) => passwordResetEmail.trim() !== customerEmail;
const getFieldErrorTranslateKey = (isExistingEmail, authorizationFailedOrInvalidEmail) => {
    if (!isExistingEmail && !authorizationFailedOrInvalidEmail) {
        return '';
    }
    return isExistingEmail ? 'user_exist_error_message' : 'invalid_email_authentication_failed';
};
const YetToTransitionView = ({ textFieldProps, showModal, handleOnClose, closeIcon, isError, passwordResetEmail, translateErrorKey, isChangeEmailView = false, negativeButtonTextKey, positiveButtonTextKey, positiveButtonProps, negativeButtonProps, headlineKey, descriptionKey, }) => {
    const translate = useTranslate();
    const errorMessage = translateErrorKey ? translate(translateErrorKey) : '';
    const emailFieldElm = useRef(null);
    useEffect(() => {
        if (isChangeEmailView && emailFieldElm && emailFieldElm.current) {
            emailFieldElm.current.focus();
        }
    }, []);
    return (
    // @ts-expect-error
    React.createElement(MessageDialog, { isShown: showModal, graphic: React.createElement(AccountSecurity, null), size: Size.L, closeDialog: handleOnClose, closeLabel: translate('close_tooltip'), closeIcon: closeIcon, title: translate(headlineKey), text: React.createElement(AlignTextCenter, null, translate(descriptionKey)), negativeButtonText: translate(negativeButtonTextKey), positiveButtonText: translate(positiveButtonTextKey), positiveButtonProps: positiveButtonProps, negativeButtonProps: negativeButtonProps },
        React.createElement(FieldWrapper, null,
            React.createElement(TextField, { id: "form-email", error: isError, errorMessage: errorMessage, type: TextFieldType.EMAIL, name: "email", label: translate('email_label'), placeholder: translate('placeholder_email'), value: passwordResetEmail, ref: emailFieldElm, ...textFieldProps }))));
};
export const YetToTransition = ({ showModal, handleOnClose, closable, passwordResetEmail, localeCode, redirectPathOnClose, setPasswordResetEmail, config, customer, }) => {
    const [state, dispatch] = useReducerState();
    const [isChangeEmailView, setIsChangeEmailView] = useState(false);
    const [isError, setError] = useState(false);
    const { isExistingEmail, authorizationFailedOrInvalidEmail } = getFacebookFormErrors(state);
    useEffect(() => {
        if (isExistingEmail || authorizationFailedOrInvalidEmail) {
            setError(true);
        }
    }, [isExistingEmail, authorizationFailedOrInvalidEmail]);
    useEffect(() => {
        loadFacebookSdk(window, config)(dispatch);
    }, []);
    const handleEmailChangeOnBlur = useCallback(e => {
        dispatch(setFacebookUserEmailTo(e.target.value));
    }, [setFacebookUserEmailTo]);
    const handleEmailChange = (e) => {
        if (isError) {
            setError(false);
            dispatch(setFacebookUserEmailUpdateFailed({
                isExistingEmail: false,
                authorizationFailedOrInvalidEmail: false,
            }));
        }
        setPasswordResetEmail(e.target.value || '');
    };
    const handleSSORedirect = async () => {
        const ssoUrl = await getSSOUrl(localeCode, {
            view: 'recover',
            passwordResetEmail,
            redirectionPath: redirectPathOnClose || '',
        });
        window.location.href = ssoUrl;
    };
    const handleResetPassword = useCallback(() => {
        track(TrackEvent.RESET_PASSWORD, {
            eventCallback() {
                handleSSORedirect();
            },
            eventTimeout: 2000,
        });
    }, [getSSOUrl, passwordResetEmail]);
    const handleFBAuthentication = () => {
        if (isPasswordEmailChanged(passwordResetEmail, customer.email)) {
            if (!validateEmail(passwordResetEmail)) {
                track(TrackEvent.CONFRIM_EMAIL);
                dispatch(setFacebookUserEmailUpdateFailed({
                    isExistingEmail: false,
                    authorizationFailedOrInvalidEmail: true,
                }));
                return;
            }
            fetchFacebookToken(window, handleResetPassword)(dispatch, state);
        }
        else {
            handleResetPassword();
        }
    };
    const translateErrorKey = getFieldErrorTranslateKey(isExistingEmail, authorizationFailedOrInvalidEmail);
    const props = {
        showModal,
        translateErrorKey,
        handleOnClose,
        closeIcon: closable,
        isError,
        passwordResetEmail,
    };
    if (isChangeEmailView) {
        return (React.createElement(YetToTransitionView, { isChangeEmailView: true, positiveButtonTextKey: "save_email", negativeButtonTextKey: "cancel", positiveButtonProps: {
                onClick: handleFBAuthentication,
            }, negativeButtonProps: {
                onClick: () => {
                    setPasswordResetEmail(customer.email);
                    setIsChangeEmailView(false);
                },
            }, textFieldProps: {
                disabled: false,
                onBlur: handleEmailChangeOnBlur,
                onChange: handleEmailChange,
            }, headlineKey: "email_change_headline", descriptionKey: "email_change_description", ...props }));
    }
    return (React.createElement(YetToTransitionView, { positiveButtonTextKey: "send_reset_link", negativeButtonTextKey: "use_different_email", positiveButtonProps: {
            onClick: handleResetPassword,
        }, negativeButtonProps: {
            onClick: () => {
                track(TrackEvent.USE_DIFFERENT_EMAIL);
                setIsChangeEmailView(true);
            },
        }, textFieldProps: {
            disabled: true,
        }, headlineKey: "headline", descriptionKey: "description", ...props }));
};
//# sourceMappingURL=yetToTransition.js.map